import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';

import { fetchConversationChannelsData } from './fetchConversationChannelsData';
import { removeConversationFromStore } from './removeConversationFromStore';
import type { ChatStoreAction } from './types';

export const deleteConversation =
	({ conversationId }: { conversationId: string }): ChatStoreAction =>
	async ({ dispatch, getState }, { assistanceService, experienceTracker }) => {
		const { promptHistory, conversations } = getState();
		const promptHistoryContext = promptHistory
			? {
					requestToken: promptHistory.requestToken,
					sessionId: conversations[conversationId]?.backendConversationId ?? conversationId,
				}
			: undefined;
		experienceTracker.start({
			name: ExperienceName.CLEAR_CHAT,
			id: conversationId,
			attributes: {
				conversationId,
			},
		});

		await getState().conversations[conversationId]?.storeAbortController?.abort();

		try {
			await assistanceService.deleteChat(conversationId);
		} catch (error: unknown) {
			experienceTracker.fail({
				teamName: 'Rovo Chat',
				name: ExperienceName.CLEAR_CHAT,
				error: error instanceof Error ? error : new Error(String(error)),
			});

			throw error;
		}

		// Remove the deleted conversation from the local store prior to refetching channels.
		// fetchConversationChannelsData preserves any local conversation whose ID is missing
		// from the API response (to keep optimistic / in-flight conversations alive). Without
		// this explicit removal, the just-deleted conversation would be treated as
		// "optimistic" and re-added to the store, causing it to reappear in the UI.
		dispatch(removeConversationFromStore({ conversationId, promptHistoryContext }));

		await dispatch(fetchConversationChannelsData({ reconcileFirstPage: true }));

		experienceTracker.succeed({
			name: ExperienceName.CLEAR_CHAT,
		});
	};
